import React, { useState, useMemo } from 'react';
import { Plus, RefreshCw, ChevronLeft, ChevronRight, Wallet } from 'lucide-react';
import { Card, SectionHeader, Btn, Badge, StatusBadge, Modal, Field, Input, Select, ModalRow, EmptyState, Table, TR, TD } from '../components/UI';
import { today, fmtDate, fmtAmt, ordinal, nowMonth, getMonthLabel, getDueDateInMonth, isDueTodayInMonth, fetchUsdRate } from '../utils/helpers';
import { onPayroll } from '../utils/hr';

// ─── Commission rate for marketing guy ───
const COMMISSION_RATE_USD = 1.5;

// Shift a YYYY-MM string by n months
function shiftMonth(ym, n) {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m - 1 + n, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export default function FinancePage({ store, persist, showToast }) {
  const {
    employees: allEmployees = [], salaryPayments = [], subscriptions = [],
    oneTimeExpenses = [], oneTimeRevenue = [], commissionPayments = [], invoices = [], clients = [],
  } = store;
  // Salaries: only people on payroll (an agent / freelancer with no salary is left out).
  const employees = allEmployees.filter(e => onPayroll(store, e));

  const currentMonth = nowMonth();
  // selectedYm = the month the salary view is focused on (defaults to current)
  const [selectedYm, setSelectedYm] = useState(currentMonth);
  const ym = selectedYm;
  const isCurrentMonth = selectedYm === currentMonth;
  const [activeTab, setActiveTab] = useState('salaries');

  // Modals
  const [showEmpModal, setShowEmpModal]         = useState(false);
  const [showOneTimeEmpModal, setShowOneTimeEmpModal] = useState(false);
  const [showCommModal, setShowCommModal]       = useState(false);
  const [showSubModal, setShowSubModal]         = useState(false);
  const [showExpModal, setShowExpModal]         = useState(false);
  const [showRevModal, setShowRevModal]         = useState(false);
  const [editingEmp, setEditingEmp]             = useState(null);
  const [editingSub, setEditingSub]             = useState(null);

  const [empForm, setEmpForm]     = useState({ name: '', amount: '', dueDay: '' });
  const [otEmpForm, setOtEmpForm] = useState({ name: '', description: '', amount: '', date: today() });
  const [commForm, setCommForm]   = useState({ proposals: '', rate: null, note: '' });
  const [subForm, setSubForm]     = useState({ name: '', amount: '', dueDay: '' });
  const [expForm, setExpForm]     = useState({ description: '', amount: '', date: today(), category: 'other' });
  const [revForm, setRevForm]     = useState({ description: '', amount: '', date: today() });
  const [fetchingRate, setFetchingRate] = useState(false);
  const [liveRate, setLiveRate]   = useState(null);

  // ─── Helpers ───
  function salaryPaidInMonth(empId, monthYear) {
    return salaryPayments.some(p => p.empId === empId && p.monthYear === monthYear);
  }
  function salaryPaidThisMonth(empId) {
    return salaryPaidInMonth(empId, ym);
  }

  function markSalaryPaid(emp) {
    // Record the payment against the SELECTED month, not always the current one
    const updated = { ...store, salaryPayments: [...salaryPayments, { id: Date.now().toString(), empId: emp.id, name: emp.name, amount: emp.amount, date: today(), monthYear: ym }] };
    persist(updated); showToast(`${emp.name}'s salary marked as paid for ${getMonthLabel(ym)}.`);
  }

  function markSalaryUnpaid(empId) {
    const removedIds = salaryPayments.filter(p => p.empId === empId && p.monthYear === ym).map(p => p.id);
    const updated = { ...store, salaryPayments: salaryPayments.filter(p => !(p.empId === empId && p.monthYear === ym)) };
    persist(updated, { deletedIds: { salaryPayments: removedIds } }); showToast('Marked as unpaid.');
  }

  // ─── Outstanding calculations ───
  const outstanding = useMemo(() => {
    // An employee "owes" salary for a month if: that month is >= their start and not paid.
    // We treat the employee's earliest known month as the first month they appear OR
    // simply count from the month they were added. To keep it simple and accurate to
    // what the app knows, we count unpaid months from the first recorded salary activity
    // OR the current employee set across all months that have any payment history.

    // Current month outstanding (fixed employees only)
    const currentDue = employees.reduce((sum, e) => {
      return salaryPaidInMonth(e.id, currentMonth) ? sum : sum + Number(e.amount || 0);
    }, 0);
    const currentUnpaidCount = employees.filter(e => !salaryPaidInMonth(e.id, currentMonth)).length;

    // Backlog: all PAST months (before current) where a fixed employee was not paid.
    // We only look back as far as the earliest payment record in the system, so we don't
    // invent infinite history. If there are no payments yet, backlog is 0.
    const allMonths = new Set(salaryPayments.filter(p => p.type !== 'onetime').map(p => p.monthYear));
    // Also include months between earliest record and now
    let earliest = currentMonth;
    salaryPayments.filter(p => p.type !== 'onetime').forEach(p => {
      if (p.monthYear < earliest) earliest = p.monthYear;
    });

    // Build list of past months from earliest up to (but not including) current
    const pastMonths = [];
    let cursor = earliest;
    while (cursor < currentMonth) {
      pastMonths.push(cursor);
      cursor = shiftMonth(cursor, 1);
    }

    let backlogAmount = 0;
    const backlogDetail = [];
    pastMonths.forEach(m => {
      employees.forEach(e => {
        if (!salaryPaidInMonth(e.id, m)) {
          backlogAmount += Number(e.amount || 0);
          backlogDetail.push({ month: m, name: e.name, amount: Number(e.amount || 0) });
        }
      });
    });

    return { currentDue, currentUnpaidCount, backlogAmount, backlogDetail, pastMonths };
  }, [employees, salaryPayments, currentMonth, ym]);

  // ─── Employee CRUD ───
  function openAddEmp() { setEditingEmp(null); setEmpForm({ name: '', amount: '', dueDay: '' }); setShowEmpModal(true); }
  function openEditEmp(e) { setEditingEmp(e); setEmpForm({ name: e.name, amount: e.amount, dueDay: e.dueDay }); setShowEmpModal(true); }

  function saveEmployee() {
    const { name, amount, dueDay } = empForm;
    if (!name || !amount || !dueDay) return showToast('All fields required.', 'error');
    const updated = { ...store, employees: [...allEmployees] };
    if (editingEmp) {
      updated.employees = updated.employees.map(e => e.id === editingEmp.id ? { ...e, name, amount: Number(amount), dueDay: Number(dueDay) } : e);
      showToast('Employee updated.');
    } else {
      updated.employees.push({ id: Date.now().toString(), name, amount: Number(amount), dueDay: Number(dueDay), type: 'fixed' });
      showToast('Employee added.');
    }
    persist(updated); setShowEmpModal(false);
  }

  function deleteEmployee(id) {
    if (!window.confirm('Remove this employee?')) return;
    const removedSalaryIds = salaryPayments.filter(p => p.empId === id).map(p => p.id);
    persist({ ...store, employees: allEmployees.filter(e => e.id !== id), salaryPayments: salaryPayments.filter(p => p.empId !== id) }, { deletedIds: { employees: [id], salaryPayments: removedSalaryIds } });
    showToast('Employee removed.');
  }

  // ─── One-time employee payment ───
  function saveOneTimeEmp() {
    const { name, description, amount, date } = otEmpForm;
    if (!name || !amount) return showToast('Name and amount required.', 'error');
    persist({ ...store, salaryPayments: [...salaryPayments, { id: Date.now().toString(), empId: 'onetime', name, description, amount: Number(amount), date, monthYear: date.slice(0,7), type: 'onetime' }] });
    setShowOneTimeEmpModal(false); showToast('One-time payment recorded.');
  }

  // ─── Commission payment ───
  async function openCommModal() {
    setCommForm({ proposals: '', rate: null, note: '' });
    setLiveRate(null);
    setShowCommModal(true);
    setFetchingRate(true);
    const rate = await fetchUsdRate();
    setLiveRate(rate);
    setFetchingRate(false);
  }

  function saveCommission() {
    const { proposals } = commForm;
    if (!proposals || !liveRate) return showToast('Enter proposals and ensure rate is fetched.', 'error');
    const usdAmt = Number(proposals) * COMMISSION_RATE_USD;
    const pkrAmt = Math.round(usdAmt * liveRate);
    persist({ ...store, commissionPayments: [...commissionPayments, { id: Date.now().toString(), proposals: Number(proposals), ratePerProposal: COMMISSION_RATE_USD, usdAmount: usdAmt, pkrAmount: pkrAmt, usdRate: liveRate, date: today(), monthYear: ym }] });
    setShowCommModal(false);
    showToast(`Commission: PKR ${pkrAmt.toLocaleString('en-PK')} recorded.`);
  }

  const commCalcPKR = commForm.proposals && liveRate ? Math.round(Number(commForm.proposals) * COMMISSION_RATE_USD * liveRate) : null;

  // ─── Subscriptions ───
  function openAddSub() { setEditingSub(null); setSubForm({ name: '', amount: '', dueDay: '' }); setShowSubModal(true); }
  function openEditSub(s) { setEditingSub(s); setSubForm({ name: s.name, amount: s.amount, dueDay: s.dueDay }); setShowSubModal(true); }

  function saveSub() {
    const { name, amount, dueDay } = subForm;
    if (!name || !amount || !dueDay) return showToast('All fields required.', 'error');
    const updated = { ...store, subscriptions: [...subscriptions] };
    if (editingSub) {
      updated.subscriptions = updated.subscriptions.map(s => s.id === editingSub.id ? { ...s, name, amount: Number(amount), dueDay: Number(dueDay) } : s);
      showToast('Subscription updated.');
    } else {
      updated.subscriptions.push({ id: Date.now().toString(), name, amount: Number(amount), dueDay: Number(dueDay) });
      showToast('Subscription added.');
    }
    persist(updated); setShowSubModal(false);
  }

  function deleteSub(id) {
    persist({ ...store, subscriptions: subscriptions.filter(s => s.id !== id) }, { deletedIds: { subscriptions: [id] } });
    showToast('Subscription removed.');
  }

  // ─── One-time expense ───
  function saveExpense() {
    const { description, amount, date, category } = expForm;
    if (!description || !amount) return showToast('Description and amount required.', 'error');
    persist({ ...store, oneTimeExpenses: [...oneTimeExpenses, { id: Date.now().toString(), description, amount: Number(amount), date, category }] });
    setShowExpModal(false); showToast('Expense recorded.');
  }

  function deleteExpense(id) {
    persist({ ...store, oneTimeExpenses: oneTimeExpenses.filter(e => e.id !== id) }, { deletedIds: { oneTimeExpenses: [id] } });
    showToast('Expense removed.');
  }

  // ─── One-time revenue ───
  function saveRevenue() {
    const { description, amount, date } = revForm;
    if (!description || !amount) return showToast('Description and amount required.', 'error');
    persist({ ...store, oneTimeRevenue: [...oneTimeRevenue, { id: Date.now().toString(), description, amount: Number(amount), date, source: 'manual' }] });
    setShowRevModal(false); showToast('Revenue recorded.');
  }

  function deleteRevenue(id) {
    persist({ ...store, oneTimeRevenue: oneTimeRevenue.filter(r => r.id !== id) }, { deletedIds: { oneTimeRevenue: [id] } });
    showToast('Revenue entry removed.');
  }

  const tabs = [
    { id: 'salaries',      label: 'Salaries' },
    { id: 'subscriptions', label: 'Subscriptions' },
    { id: 'expenses',      label: 'One-time expenses' },
    { id: 'revenue',       label: 'Revenue' },
  ];

  const dueThisMonthSalaries = employees.filter(e => isDueTodayInMonth(e.dueDay));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Page header */}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:16, flexWrap:'wrap' }}>
        <div>
          <h1 className="t-h1">Finance Money Hub</h1>
          <p className="t-sub" style={{ marginTop:4 }}>Manage payroll, salary disbursements and backlogs.</p>
        </div>
      </div>

      {/* Tab bar */}
      <div className="segmented">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} className={activeTab === t.id ? 'active' : ''}>{t.label}</button>
        ))}
      </div>

      {/* ── SALARIES ── */}
      {activeTab === 'salaries' && (
        <>
          {/* Outstanding summary cards */}
          <div className="grid-salary">
            <Card style={{ padding: '22px 24px' }}>
              <div style={{ display:'flex', alignItems:'center', gap:9, marginBottom:10 }}>
                <Wallet size={16} color="var(--accent)"/>
                <span className="t-label">Outstanding — this month</span>
              </div>
              <div className="t-currency fig-lg" style={{ letterSpacing:'-0.02em', color: outstanding.currentDue > 0 ? 'var(--text)' : 'var(--green)' }}>{fmtAmt(outstanding.currentDue)}</div>
              <div style={{ fontSize: 13, color: 'var(--text3)', marginTop: 6 }}>
                {outstanding.currentUnpaidCount === 0 ? 'All salaries paid for ' + getMonthLabel(currentMonth) : <><span style={{color:'var(--red)',fontWeight:700}}>{outstanding.currentUnpaidCount} salar{outstanding.currentUnpaidCount > 1 ? 'ies' : 'y'}</span> pending this month</>}
              </div>
            </Card>
            <Card style={{ padding: '22px 24px' }}>
              <div style={{ display:'flex', alignItems:'center', gap:9, marginBottom:10 }}>
                <RefreshCw size={15} color="var(--red)"/>
                <span className="t-label">Accumulated backlog</span>
              </div>
              <div className="t-currency fig-lg" style={{ letterSpacing:'-0.02em', color: outstanding.backlogAmount > 0 ? 'var(--red)' : 'var(--green)' }}>{fmtAmt(outstanding.backlogAmount)}</div>
              <div style={{ fontSize: 13, color: 'var(--text3)', marginTop: 6 }}>
                {outstanding.backlogAmount === 0 ? 'No unpaid past months' : `${outstanding.backlogDetail.length} unpaid salar${outstanding.backlogDetail.length > 1 ? 'ies' : 'y'} across past months`}
              </div>
            </Card>
          </div>

          {/* Month selector */}
          <div style={{ display:'flex', justifyContent:'flex-start' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Btn size="sm" variant="default" onClick={() => setSelectedYm(shiftMonth(selectedYm, -1))} style={{padding:'8px 10px'}}><ChevronLeft size={15} /></Btn>
              <div style={{ textAlign: 'center', minWidth: 180 }}>
                <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--text)', fontFamily: 'var(--mono)', fontVariantNumeric: 'lining-nums tabular-nums' }}>{getMonthLabel(ym)}</div>
                {!isCurrentMonth && <div style={{ fontSize: 11, color: 'var(--accent2)', marginTop: 2 }}>Past/future month · <span style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => setSelectedYm(currentMonth)}>back to current</span></div>}
                {isCurrentMonth && <div className="t-label" style={{ marginTop: 3 }}>Current month</div>}
              </div>
              <Btn size="sm" variant="default" onClick={() => setSelectedYm(shiftMonth(selectedYm, 1))} style={{padding:'8px 10px'}}><ChevronRight size={15} /></Btn>
            </div>
          </div>

          {/* Fixed monthly salaries — for the SELECTED month */}
          <Card>
            <SectionHeader
              title={`Salaries — ${getMonthLabel(ym)}`}
              count={employees.length}
              action={
                <div style={{ display: 'flex', gap: 8 }}>
                  <Btn size="sm" onClick={() => setShowOneTimeEmpModal(true)}><Plus size={12} /> One-time payment</Btn>
                  <Btn size="sm" onClick={openAddEmp}><Plus size={12} /> Add employee</Btn>
                </div>
              }
            />
            {employees.length === 0 ? <EmptyState message="No employees." /> : (
              <Table headers={['Name', 'Monthly amount', 'Due day', getMonthLabel(ym), 'Actions']}>
                {employees.sort((a,b) => a.dueDay - b.dueDay).map(emp => {
                  const paid = salaryPaidThisMonth(emp.id);
                  const dueToday = isCurrentMonth && isDueTodayInMonth(emp.dueDay);
                  return (
                    <TR key={emp.id} highlight={dueToday && !paid}>
                      <TD bold>{emp.name}{dueToday && !paid && <span style={{ marginLeft: 8, fontSize: 10, color: 'var(--accent)', background: 'rgba(169,137,83,0.12)', padding: '2px 7px', borderRadius: 'var(--radius)', fontWeight: 600, fontFamily: 'var(--mono)' }}>due today</span>}</TD>
                      <TD mono>{fmtAmt(emp.amount)}</TD>
                      <TD muted>{ordinal(emp.dueDay)} of month</TD>
                      <TD><StatusBadge status={paid ? 'paid' : 'unpaid'} /></TD>
                      <TD>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {!paid ? <Btn size="sm" variant="success" onClick={() => markSalaryPaid(emp)}>Mark paid</Btn>
                                 : <Btn size="sm" variant="ghost" onClick={() => markSalaryUnpaid(emp.id)}>Undo</Btn>}
                          <Btn size="sm" variant="ghost" onClick={() => openEditEmp(emp)}>Edit</Btn>
                          <Btn size="sm" variant="danger" onClick={() => deleteEmployee(emp.id)}>Remove</Btn>
                        </div>
                      </TD>
                    </TR>
                  );
                })}
              </Table>
            )}
            {/* Month total footer */}
            <div style={{ padding: '12px 18px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
              <span style={{ color: 'var(--text3)' }}>Paid this month / Total payroll</span>
              <span style={{ fontFamily: 'var(--mono)', fontWeight: 700, fontVariantNumeric: 'lining-nums tabular-nums' }}>
                <span style={{ color: 'var(--green)' }}>{fmtAmt(employees.filter(e => salaryPaidThisMonth(e.id)).reduce((s, e) => s + Number(e.amount || 0), 0))}</span>
                <span style={{ color: 'var(--text3)' }}> / {fmtAmt(employees.reduce((s, e) => s + Number(e.amount || 0), 0))}</span>
              </span>
            </div>
          </Card>

          {/* Backlog detail — only if there's a backlog */}
          {outstanding.backlogDetail.length > 0 && (
            <Card>
              <SectionHeader title="Unpaid backlog (past months)" count={outstanding.backlogDetail.length} />
              <Table headers={['Month', 'Employee', 'Amount', 'Action']}>
                {outstanding.backlogDetail.slice().sort((a, b) => b.month.localeCompare(a.month)).map((d, i) => {
                  const emp = employees.find(e => e.name === d.name);
                  return (
                    <TR key={i}>
                      <TD muted>{getMonthLabel(d.month)}</TD>
                      <TD bold>{d.name}</TD>
                      <TD mono>{fmtAmt(d.amount)}</TD>
                      <TD>
                        <Btn size="sm" variant="success" onClick={() => {
                          if (!emp) return;
                          persist({ ...store, salaryPayments: [...salaryPayments, { id: Date.now().toString(), empId: emp.id, name: emp.name, amount: emp.amount, date: today(), monthYear: d.month }] });
                          showToast(`${d.name}'s salary marked paid for ${getMonthLabel(d.month)}.`);
                        }}>Mark paid</Btn>
                      </TD>
                    </TR>
                  );
                })}
              </Table>
            </Card>
          )}

          {/* Payment history — all months */}
          <Card>
            <SectionHeader title="Payment history" count={salaryPayments.filter(p => p.type !== 'onetime').length} />
            {salaryPayments.filter(p => p.type !== 'onetime').length === 0 ? <EmptyState message="No salary payments recorded yet." /> : (
              <Table headers={['Month', 'Employee', 'Amount', 'Paid on']}>
                {salaryPayments.filter(p => p.type !== 'onetime').slice().sort((a, b) => (b.monthYear + b.date).localeCompare(a.monthYear + a.date)).map(p => (
                  <TR key={p.id}>
                    <TD muted>{getMonthLabel(p.monthYear)}</TD>
                    <TD bold>{p.name}</TD>
                    <TD mono>{fmtAmt(p.amount)}</TD>
                    <TD muted>{fmtDate(p.date)}</TD>
                  </TR>
                ))}
              </Table>
            )}
          </Card>

          {/* Commission marketing guy */}
          <Card>
            <SectionHeader
              title="Commission — marketing"
              action={<Btn size="sm" onClick={openCommModal}><Plus size={12} /> Record this month</Btn>}
            />
            <div style={{ padding: '12px 18px', borderBottom: '1px solid var(--border)', fontSize: 12, color: 'var(--text3)' }}>
              Rate: <strong style={{ color: 'var(--text2)' }}>$1.50 per proposal</strong> · Paid in PKR at live USD→PKR rate on calculation day
            </div>
            {commissionPayments.length === 0 ? <EmptyState message="No commission payments recorded yet." /> : (
              <Table headers={['Date', 'Proposals', 'USD rate', 'USD amount', 'PKR amount']}>
                {commissionPayments.slice().reverse().map(p => (
                  <TR key={p.id}>
                    <TD muted>{fmtDate(p.date)}</TD>
                    <TD mono>{p.proposals}</TD>
                    <TD mono muted>{p.usdRate ? `1 USD = PKR ${Number(p.usdRate).toFixed(2)}` : '—'}</TD>
                    <TD mono>{fmtAmt(p.usdAmount, 'USD')}</TD>
                    <TD mono bold>{fmtAmt(p.pkrAmount)}</TD>
                  </TR>
                ))}
              </Table>
            )}
          </Card>

          {/* One-time payments history */}
          {salaryPayments.filter(p => p.type === 'onetime').length > 0 && (
            <Card>
              <SectionHeader title="One-time payments" count={salaryPayments.filter(p => p.type === 'onetime').length} />
              <Table headers={['Date', 'Name', 'Description', 'Amount']}>
                {salaryPayments.filter(p => p.type === 'onetime').slice().reverse().map(p => (
                  <TR key={p.id}>
                    <TD muted>{fmtDate(p.date)}</TD>
                    <TD bold>{p.name}</TD>
                    <TD muted>{p.description || '—'}</TD>
                    <TD mono>{fmtAmt(p.amount)}</TD>
                  </TR>
                ))}
              </Table>
            </Card>
          )}
        </>
      )}

      {/* ── SUBSCRIPTIONS ── */}
      {activeTab === 'subscriptions' && (
        <Card>
          <SectionHeader title="Monthly subscriptions" count={subscriptions.length} action={<Btn size="sm" onClick={openAddSub}><Plus size={12} /> Add subscription</Btn>} />
          {subscriptions.length === 0 ? <EmptyState message="No subscriptions added yet." /> : (
            <Table headers={['Name', 'Monthly cost', 'Billing day', 'Next due', 'Actions']}>
              {subscriptions.sort((a,b) => a.dueDay - b.dueDay).map(s => (
                <TR key={s.id} highlight={isDueTodayInMonth(s.dueDay)}>
                  <TD bold>
                    {s.name}
                    {isDueTodayInMonth(s.dueDay) && <span style={{ marginLeft: 8, fontSize: 10, color: 'var(--accent)', background: 'rgba(169,137,83,0.12)', padding: '2px 7px', borderRadius: 'var(--radius)', fontWeight: 600, fontFamily: 'var(--mono)' }}>due today</span>}
                  </TD>
                  <TD mono>{fmtAmt(s.amount)}</TD>
                  <TD muted>{ordinal(s.dueDay)}</TD>
                  <TD muted>{fmtDate(getDueDateInMonth(s.dueDay, nowMonth()))}</TD>
                  <TD>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <Btn size="sm" variant="ghost" onClick={() => openEditSub(s)}>Edit</Btn>
                      <Btn size="sm" variant="danger" onClick={() => deleteSub(s.id)}>Remove</Btn>
                    </div>
                  </TD>
                </TR>
              ))}
            </Table>
          )}
          <div style={{ padding: '12px 18px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
            <span style={{ color: 'var(--text3)' }}>Total monthly</span>
            <span style={{ fontWeight: 700, color: 'var(--red)', fontFamily: 'var(--mono)', fontVariantNumeric: 'lining-nums tabular-nums' }}>{fmtAmt(subscriptions.reduce((s, x) => s + Number(x.amount || 0), 0))}</span>
          </div>
        </Card>
      )}

      {/* ── ONE-TIME EXPENSES ── */}
      {activeTab === 'expenses' && (
        <Card>
          <SectionHeader
            title="One-time expenses"
            count={oneTimeExpenses.length}
            action={<Btn size="sm" onClick={() => { setExpForm({ description: '', amount: '', date: today(), category: 'other' }); setShowExpModal(true); }}><Plus size={12} /> Add expense</Btn>}
          />
          {oneTimeExpenses.length === 0 ? <EmptyState message="No one-time expenses recorded." /> : (
            <Table headers={['Date', 'Description', 'Category', 'Amount', 'Actions']}>
              {oneTimeExpenses.slice().sort((a,b) => b.date.localeCompare(a.date)).map(e => (
                <TR key={e.id}>
                  <TD muted>{fmtDate(e.date)}</TD>
                  <TD bold>{e.description}</TD>
                  <TD><Badge label={e.category || 'other'} color="orange" /></TD>
                  <TD mono>{fmtAmt(e.amount)}</TD>
                  <TD><Btn size="sm" variant="danger" onClick={() => deleteExpense(e.id)}>Remove</Btn></TD>
                </TR>
              ))}
            </Table>
          )}
        </Card>
      )}

      {/* ── REVENUE ── */}
      {activeTab === 'revenue' && (
        <Card>
          <SectionHeader
            title="Revenue"
            count={oneTimeRevenue.length}
            action={<Btn size="sm" onClick={() => { setRevForm({ description: '', amount: '', date: today() }); setShowRevModal(true); }}><Plus size={12} /> Add revenue</Btn>}
          />
          <div style={{ padding: '10px 18px', borderBottom: '1px solid var(--border)', fontSize: 12, color: 'var(--text3)' }}>
            Invoice payments are recorded automatically here when marked as paid. You can also add manual entries below.
          </div>
          {oneTimeRevenue.length === 0 ? <EmptyState message="No revenue recorded yet." /> : (
            <Table headers={['Date', 'Description', 'Source', 'Amount', 'Actions']}>
              {oneTimeRevenue.slice().sort((a,b) => b.date.localeCompare(a.date)).map(r => (
                <TR key={r.id}>
                  <TD muted>{fmtDate(r.date)}</TD>
                  <TD bold>{r.description}</TD>
                  <TD><Badge label={r.source === 'invoice' ? 'Invoice' : 'Manual'} color={r.source === 'invoice' ? 'blue' : 'green'} /></TD>
                  <TD mono style={{ color: 'var(--green)', fontWeight: 600 }}>{fmtAmt(r.amount)}</TD>
                  <TD>{r.source !== 'invoice' && <Btn size="sm" variant="danger" onClick={() => deleteRevenue(r.id)}>Remove</Btn>}</TD>
                </TR>
              ))}
            </Table>
          )}
        </Card>
      )}

      {/* ── MODALS ── */}

      {/* Add/Edit Employee */}
      <Modal open={showEmpModal} onClose={() => setShowEmpModal(false)} title={editingEmp ? 'Edit employee' : 'Add employee'}>
        <Field label="Full name"><Input value={empForm.name} onChange={e => setEmpForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Ahmed Ali" autoFocus /></Field>
        <ModalRow>
          <Field label="Monthly salary (PKR)"><Input type="number" value={empForm.amount} onChange={e => setEmpForm(p => ({ ...p, amount: e.target.value }))} placeholder="e.g. 50000" /></Field>
          <Field label="Due day of month"><Input type="number" min={1} max={28} value={empForm.dueDay} onChange={e => setEmpForm(p => ({ ...p, dueDay: e.target.value }))} placeholder="e.g. 1" /></Field>
        </ModalRow>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: '1.25rem' }}>
          <Btn onClick={() => setShowEmpModal(false)}>Cancel</Btn>
          <Btn variant="primary" onClick={saveEmployee}>Save</Btn>
        </div>
      </Modal>

      {/* One-time employee payment */}
      <Modal open={showOneTimeEmpModal} onClose={() => setShowOneTimeEmpModal(false)} title="Record one-time payment">
        <Field label="Person name"><Input value={otEmpForm.name} onChange={e => setOtEmpForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Freelancer name" autoFocus /></Field>
        <Field label="Description"><Input value={otEmpForm.description} onChange={e => setOtEmpForm(p => ({ ...p, description: e.target.value }))} placeholder="e.g. Website redesign project" /></Field>
        <ModalRow>
          <Field label="Amount (PKR)"><Input type="number" value={otEmpForm.amount} onChange={e => setOtEmpForm(p => ({ ...p, amount: e.target.value }))} placeholder="e.g. 25000" /></Field>
          <Field label="Date"><Input type="date" value={otEmpForm.date} onChange={e => setOtEmpForm(p => ({ ...p, date: e.target.value }))} /></Field>
        </ModalRow>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: '1.25rem' }}>
          <Btn onClick={() => setShowOneTimeEmpModal(false)}>Cancel</Btn>
          <Btn variant="primary" onClick={saveOneTimeEmp}>Record payment</Btn>
        </div>
      </Modal>

      {/* Commission modal */}
      <Modal open={showCommModal} onClose={() => setShowCommModal(false)} title="Record marketing commission">
        <div style={{ background: 'var(--bg3)', borderRadius: 'var(--radius)', padding: '0.875rem 1rem', marginBottom: '1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 13, color: 'var(--text3)' }}>Live USD → PKR rate</span>
          {fetchingRate
            ? <span style={{ fontSize: 12, color: 'var(--text3)', display: 'flex', alignItems: 'center', gap: 6 }}><RefreshCw size={12} style={{ animation: 'spin 1s linear infinite' }} /> Fetching...</span>
            : liveRate
              ? <span style={{ fontWeight: 700, color: 'var(--green)', fontFamily: 'var(--mono)', fontSize: 14, fontVariantNumeric: 'lining-nums tabular-nums' }}>1 USD = PKR {Number(liveRate).toFixed(2)}</span>
              : <Btn size="sm" onClick={async () => { setFetchingRate(true); const r = await fetchUsdRate(); setLiveRate(r); setFetchingRate(false); }}>Retry fetch</Btn>
          }
        </div>
        <Field label="Number of proposals sent this month" hint={`Rate: $${COMMISSION_RATE_USD} per proposal`}>
          <Input type="number" value={commForm.proposals} onChange={e => setCommForm(p => ({ ...p, proposals: e.target.value }))} placeholder="e.g. 40" autoFocus />
        </Field>
        {commCalcPKR !== null && (
          <div style={{ background: 'var(--bg3)', borderRadius: 'var(--radius)', padding: '0.875rem 1rem', marginBottom: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontSize: 12, color: 'var(--text3)' }}>{commForm.proposals} proposals × ${COMMISSION_RATE_USD}</span>
              <span style={{ fontSize: 12, color: 'var(--text2)', fontFamily: 'var(--mono)', fontVariantNumeric: 'lining-nums tabular-nums' }}>${(Number(commForm.proposals) * COMMISSION_RATE_USD).toFixed(2)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 13, color: 'var(--text3)' }}>Total in PKR</span>
              <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--accent)', fontFamily: 'var(--mono)', fontVariantNumeric: 'lining-nums tabular-nums' }}>{fmtAmt(commCalcPKR)}</span>
            </div>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: '1.25rem' }}>
          <Btn onClick={() => setShowCommModal(false)}>Cancel</Btn>
          <Btn variant="primary" onClick={saveCommission} disabled={!commForm.proposals || !liveRate}>Record payment</Btn>
        </div>
      </Modal>

      {/* Add/Edit Subscription */}
      <Modal open={showSubModal} onClose={() => setShowSubModal(false)} title={editingSub ? 'Edit subscription' : 'Add subscription'}>
        <Field label="Subscription name"><Input value={subForm.name} onChange={e => setSubForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Adobe Creative Cloud" autoFocus /></Field>
        <ModalRow>
          <Field label="Monthly cost (PKR)"><Input type="number" value={subForm.amount} onChange={e => setSubForm(p => ({ ...p, amount: e.target.value }))} placeholder="e.g. 5000" /></Field>
          <Field label="Billing day"><Input type="number" min={1} max={28} value={subForm.dueDay} onChange={e => setSubForm(p => ({ ...p, dueDay: e.target.value }))} placeholder="e.g. 15" /></Field>
        </ModalRow>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: '1.25rem' }}>
          <Btn onClick={() => setShowSubModal(false)}>Cancel</Btn>
          <Btn variant="primary" onClick={saveSub}>Save</Btn>
        </div>
      </Modal>

      {/* Add expense */}
      <Modal open={showExpModal} onClose={() => setShowExpModal(false)} title="Record one-time expense">
        <Field label="Description"><Input value={expForm.description} onChange={e => setExpForm(p => ({ ...p, description: e.target.value }))} placeholder="e.g. Office supplies" autoFocus /></Field>
        <ModalRow>
          <Field label="Amount (PKR)"><Input type="number" value={expForm.amount} onChange={e => setExpForm(p => ({ ...p, amount: e.target.value }))} placeholder="e.g. 8000" /></Field>
          <Field label="Date"><Input type="date" value={expForm.date} onChange={e => setExpForm(p => ({ ...p, date: e.target.value }))} /></Field>
        </ModalRow>
        <Field label="Category">
          <Select value={expForm.category} onChange={e => setExpForm(p => ({ ...p, category: e.target.value }))}>
            <option value="tools">Tools</option>
            <option value="marketing">Marketing</option>
            <option value="office">Office</option>
            <option value="travel">Travel</option>
            <option value="other">Other</option>
          </Select>
        </Field>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: '1.25rem' }}>
          <Btn onClick={() => setShowExpModal(false)}>Cancel</Btn>
          <Btn variant="primary" onClick={saveExpense}>Record expense</Btn>
        </div>
      </Modal>

      {/* Add manual revenue */}
      <Modal open={showRevModal} onClose={() => setShowRevModal(false)} title="Record manual revenue">
        <Field label="Description"><Input value={revForm.description} onChange={e => setRevForm(p => ({ ...p, description: e.target.value }))} placeholder="e.g. Ad-hoc project for XYZ client" autoFocus /></Field>
        <ModalRow>
          <Field label="Amount (PKR)"><Input type="number" value={revForm.amount} onChange={e => setRevForm(p => ({ ...p, amount: e.target.value }))} placeholder="e.g. 50000" /></Field>
          <Field label="Date"><Input type="date" value={revForm.date} onChange={e => setRevForm(p => ({ ...p, date: e.target.value }))} /></Field>
        </ModalRow>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: '1.25rem' }}>
          <Btn onClick={() => setShowRevModal(false)}>Cancel</Btn>
          <Btn variant="primary" onClick={saveRevenue}>Record revenue</Btn>
        </div>
      </Modal>
    </div>
  );
}
